import React, { useEffect, useMemo, useRef, useState } from "react";

/* =======================
   NOTE / THEORY HELPERS
   ======================= */
const NOTE_NAMES = [
  "C",
  "C#",
  "D",
  "D#",
  "E",
  "F",
  "F#",
  "G",
  "G#",
  "A",
  "A#",
  "B",
];
const ALT_NOTE_NAMES: Record<string, string> = {
  Db: "C#",
  Eb: "D#",
  Gb: "F#",
  Ab: "G#",
  Bb: "A#",
};
const pc = (m: number) => ((m % 12) + 12) % 12;
const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));
function noteIndex(n: string) {
  const nn = ALT_NOTE_NAMES[n] || n;
  return NOTE_NAMES.indexOf(nn);
}
function pow2(x: number) {
  return Math.pow(2, x / 12);
}

/* =======================
   TUNINGS
   ======================= */
const STANDARD = [
  { name: "E2", midi: 40, freq: 82.4069 },
  { name: "A2", midi: 45, freq: 110.0 },
  { name: "D3", midi: 50, freq: 146.832 },
  { name: "G3", midi: 55, freq: 196.0 },
  { name: "B3", midi: 59, freq: 246.942 },
  { name: "E4", midi: 64, freq: 329.628 },
];
type Tuning = { id: string; label: string; offsets: number[] };
const TUNINGS: Tuning[] = [
  { id: "standard", label: "Standard (EADGBE)", offsets: [0, 0, 0, 0, 0, 0] },
  { id: "dropd", label: "Drop D (DADGBE)", offsets: [-2, 0, 0, 0, 0, 0] },
  {
    id: "eb",
    label: "Eb Standard (Eb Ab Db Gb Bb Eb)",
    offsets: [-1, -1, -1, -1, -1, -1],
  },
  { id: "dadgad", label: "DADGAD", offsets: [-2, 0, 0, 0, -2, 0] },
  {
    id: "openg",
    label: "Open G (D G D G B D)",
    offsets: [-2, -2, 0, 0, 0, -2],
  },
  {
    id: "opend",
    label: "Open D (D A D F# A D)",
    offsets: [-2, 0, 0, -3, 0, -2],
  },
];
function midiFor(openMidi: number, fret: number) {
  return openMidi + fret;
}
function freqFor(openFreq: number, fret: number) {
  return openFreq * pow2(fret);
}

/* =======================
   CHORD LIBRARY + SHAPES (CAGED)
   ======================= */
const OPEN_CHORDS: Record<string, Record<string, number[]>> = {
  C: {
    maj: [-1, 3, 2, 0, 1, 0],
    maj7: [-1, 3, 2, 0, 0, 0],
    add9: [-1, 3, 2, 0, 3, 0],
  },
  D: {
    maj: [-1, -1, 0, 2, 3, 2],
    min: [-1, -1, 0, 2, 3, 1],
    "7": [-1, -1, 0, 2, 1, 2],
    sus2: [-1, -1, 0, 2, 3, 0],
    sus4: [-1, -1, 0, 2, 3, 3],
  },
  E: {
    maj: [0, 2, 2, 1, 0, 0],
    min: [0, 2, 2, 0, 0, 0],
    "7": [0, 2, 0, 1, 0, 0],
    m7: [0, 2, 0, 0, 0, 0],
    maj7: [0, 2, 1, 1, 0, 0],
  },
  A: {
    maj: [-1, 0, 2, 2, 2, 0],
    min: [-1, 0, 2, 2, 1, 0],
    "7": [-1, 0, 2, 0, 2, 0],
    m7: [-1, 0, 2, 0, 1, 0],
    sus2: [-1, 0, 2, 4, 0, 0],
    sus4: [-1, 0, 2, 2, 3, 0],
    add9: [-1, 0, 2, 4, 2, 0],
  },
  G: {
    maj: [3, 2, 0, 0, 0, 3],
    "7": [3, 2, 0, 0, 0, 1],
    add9: [3, 2, 0, 2, 0, 3],
  },
  Em: { maj: [0, 2, 2, 0, 0, 0] },
  Am: { maj: [-1, 0, 2, 2, 1, 0] },
  F: { maj: [1, 3, 3, 2, 1, 1] },
};

// Movable shapes (barre approximations)
const SHAPES = {
  E: {
    maj: (r: number) => [r, r + 2, r + 2, r + 1, r, r],
    min: (r: number) => [r, r + 2, r + 2, r, r, r],
  },
  A: {
    maj: (r: number) => [-1, r, r + 2, r + 2, r + 2, r],
    min: (r: number) => [-1, r, r + 2, r + 2, r + 1, r],
  },
  G: { maj: (r: number) => [r + 3, r + 2, r, r, r, r + 3] },
  C: { maj: (r: number) => [-1, r + 3, r + 2, r, r + 1, r] },
  D: {
    maj: (r: number) => [-1, -1, r, r + 2, r + 3, r + 2],
    min: (r: number) => [-1, -1, r, r + 2, r + 3, r + 1],
  },
} as const;

function rForShape(root: string, shape: "E" | "A" | "G" | "C" | "D") {
  const rootIdx = noteIndex(ALT_NOTE_NAMES[root] || root);
  const eIdx = noteIndex("E"),
    aIdx = noteIndex("A"),
    dIdx = noteIndex("D");
  switch (shape) {
    case "E":
      return (rootIdx - eIdx + 12) % 12 || 12;
    case "A":
      return (rootIdx - aIdx + 12) % 12 || 12;
    case "G":
      return ((rootIdx - eIdx + 12) % 12 || 12) - 3;
    case "C":
      return ((rootIdx - aIdx + 12) % 12 || 12) - 3;
    case "D":
      return (rootIdx - dIdx + 12) % 12 || 12;
  }
}

/* Generate voicings list (Open + CAGED up to ~15th) */
function generateVoicings(root: string, quality: string) {
  const out: { frets: number[]; baseFret: number; label: string }[] = [];
  const norm = ALT_NOTE_NAMES[root] || root;
  const open = OPEN_CHORDS[norm]?.[quality];
  if (open) out.push({ frets: open, baseFret: 1, label: "Open" });

  const add = (label: string, arr: number[]) => {
    const pos = arr.filter((x) => x > 0);
    if (!pos.length) return;
    const base = Math.min(...pos);
    if (base <= 15) out.push({ frets: arr, baseFret: base, label });
  };

  if (SHAPES.E[quality as "maj" | "min"]) {
    const r = rForShape(root, "E"),
      r2 = r + 12;
    add(`E-shape @ ${r}`, SHAPES.E[quality as "maj" | "min"](r));
    if (r2 <= 15)
      add(`E-shape @ ${r2}`, SHAPES.E[quality as "maj" | "min"](r2));
  }
  if (SHAPES.A[quality as "maj" | "min"]) {
    const r = rForShape(root, "A"),
      r2 = r + 12;
    add(`A-shape @ ${r}`, SHAPES.A[quality as "maj" | "min"](r));
    if (r2 <= 15)
      add(`A-shape @ ${r2}`, SHAPES.A[quality as "maj" | "min"](r2));
  }
  if (quality === "maj") {
    const rG = rForShape(root, "G"),
      rG2 = rG + 12;
    add(`G-shape @ ${rG}`, SHAPES.G.maj(rG));
    if (rG2 <= 15) add(`G-shape @ ${rG2}`, SHAPES.G.maj(rG2));
    const rC = rForShape(root, "C"),
      rC2 = rC + 12;
    add(`C-shape @ ${rC}`, SHAPES.C.maj(rC));
    if (rC2 <= 15) add(`C-shape @ ${rC2}`, SHAPES.C.maj(rC2));
    const rD = rForShape(root, "D"),
      rD2 = rD + 12;
    add(`D-shape @ ${rD}`, SHAPES.D.maj(rD));
    if (rD2 <= 15) add(`D-shape @ ${rD2}`, SHAPES.D.maj(rD2));
  }
  if (quality === "min") {
    const rDm = rForShape(root, "D"),
      rDm2 = rDm + 12;
    add(`D-min shape @ ${rDm}`, SHAPES.D.min(rDm));
    if (rDm2 <= 15) add(`D-min shape @ ${rDm2}`, SHAPES.D.min(rDm2));
  }

  const seen = new Set<string>();
  return out
    .filter((v) => {
      const k = `${v.label}-${v.baseFret}-${v.frets.join(",")}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .sort((a, b) => a.baseFret - b.baseFret);
}

/* =======================
   INTERVAL LABELS (for chord dots)
   ======================= */
const DEG_LABEL: Record<number, string> = {
  0: "R",
  1: "b2",
  2: "2",
  3: "b3",
  4: "3",
  5: "4",
  6: "#4/b5",
  7: "5",
  8: "#5/b6",
  9: "6",
  10: "b7",
  11: "7",
};

/* =======================
   AUDIO: GUITAR + METRONOME
   ======================= */
class GuitarAudio {
  ctx: AudioContext;
  master: GainNode;
  filter: BiquadFilterNode;
  sustainSec = 2.5;
  constructor() {
    this.ctx = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.6;
    this.filter = this.ctx.createBiquadFilter();
    this.filter.type = "lowpass";
    this.filter.frequency.value = 5500;
    this.filter.Q.value = 0.7;
    this.master.connect(this.filter).connect(this.ctx.destination);
  }
  setPickup(p: "neck" | "middle" | "bridge") {
    const map = { neck: 3500, middle: 5000, bridge: 7000 } as const;
    this.filter.frequency.setTargetAtTime(map[p], this.ctx.currentTime, 0.01);
  }
  setSustain(sec: number) {
    this.sustainSec = clamp(sec, 0.2, 8);
  }
  async resume() {
    if (this.ctx.state !== "running") await this.ctx.resume();
  }
  pluck(freq: number, when: number = 0) {
    const now = this.ctx.currentTime + when;
    const vca = this.ctx.createGain();
    vca.gain.setValueAtTime(0, now);
    vca.gain.linearRampToValueAtTime(1.0, now + 0.006);
    vca.gain.exponentialRampToValueAtTime(0.0006, now + this.sustainSec);

    const o1 = this.ctx.createOscillator();
    o1.type = "triangle";
    o1.frequency.setValueAtTime(freq * 0.999, now);
    const o2 = this.ctx.createOscillator();
    o2.type = "triangle";
    o2.frequency.setValueAtTime(freq * 1.003, now);
    const bright = this.ctx.createBiquadFilter();
    bright.type = "lowpass";
    bright.frequency.setValueAtTime(8000, now);

    const noise = this.ctx.createBufferSource();
    const nbuf = this.ctx.createBuffer(
      1,
      this.ctx.sampleRate * 0.08,
      this.ctx.sampleRate
    );
    const data = nbuf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    }
    noise.buffer = nbuf;
    const nGain = this.ctx.createGain();
    nGain.gain.setValueAtTime(0.35, now);
    nGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);

    o1.connect(bright);
    o2.connect(bright);
    bright.connect(vca).connect(this.master);
    noise.connect(nGain).connect(this.master);

    o1.start(now);
    o2.start(now);
    noise.start(now);
    o1.stop(now + this.sustainSec + 1.0);
    o2.stop(now + this.sustainSec + 1.0);
    noise.stop(now + 0.09);
  }
}
class Metronome {
  ctx: AudioContext;
  out: GainNode;
  vol = 0.4;
  constructor(ctx: AudioContext, destination: AudioNode) {
    this.ctx = ctx;
    this.out = this.ctx.createGain();
    this.out.gain.value = this.vol;
    this.out.connect(destination);
  }
  setVolume(v: number) {
    this.vol = clamp(v, 0, 1);
    this.out.gain.value = this.vol;
  }
  click(accent = false) {
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = "square";
    o.frequency.value = accent ? 1800 : 1200;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(1, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.08);
    o.connect(g).connect(this.out);
    o.start(t);
    o.stop(t + 0.09);
  }
}

/* =======================
   COMPONENT
   ======================= */
export default function StratVirtualGuitar() {
  // Core state
  const [root, setRoot] = useState("E");
  const [quality, setQuality] = useState("maj");
  const [pickup, setPickup] = useState<"neck" | "middle" | "bridge">("middle");
  const [capo, setCapo] = useState(0);
  const [sustain, setSustain] = useState(2.5);
  const [tuningId, setTuningId] = useState("standard");
  const [dragCapo, setDragCapo] = useState(false);

  // Voicings
  const [voicings, setVoicings] = useState<
    { frets: number[]; baseFret: number; label: string }[]
  >([]);
  const [voicingIdx, setVoicingIdx] = useState(0);

  // Loop & metronome
  const [isRecording, setIsRecording] = useState(false);
  const [isLooping, setIsLooping] = useState(false);
  const [bpm, setBpm] = useState(90);
  const [metroOn, setMetroOn] = useState(false);
  const [metroVol, setMetroVol] = useState(0.4);
  const [countIn, setCountIn] = useState(true);

  const recordStartRef = useRef<number>(0);
  const eventsRef = useRef<
    { t: number; kind: "pluck" | "strum"; s?: number; dir?: "down" | "up" }[]
  >([]);

  // Audio
  const audioRef = useRef<GuitarAudio | null>(null);
  const metroRef = useRef<Metronome | null>(null);
  useEffect(() => {
    const g = new GuitarAudio();
    audioRef.current = g;
    metroRef.current = new Metronome(g.ctx, g.filter);
  }, []);
  useEffect(() => {
    audioRef.current?.setPickup(pickup);
  }, [pickup]);
  useEffect(() => {
    audioRef.current?.setSustain(sustain);
  }, [sustain]);
  useEffect(() => {
    metroRef.current?.setVolume(metroVol);
  }, [metroVol]);

  // Tuning (already includes capo in midi/freq)
  const tuning = useMemo(() => {
    const preset = TUNINGS.find((t) => t.id === tuningId)!;
    return STANDARD.map((s, i) => ({
      name: s.name,
      midi: s.midi + preset.offsets[i] + capo,
      freq: s.freq * pow2(preset.offsets[i] + capo),
    }));
  }, [tuningId, capo]);

  // Open-string names (reflect current tuning + capo). Low-E is index 0 (bottom).
  const openNames = useMemo(
    () => tuning.map((s) => NOTE_NAMES[pc(s.midi)]),
    [tuning]
  );

  // Chord + voicings
  useEffect(() => {
    setVoicings(generateVoicings(root, quality));
    setVoicingIdx(0);
  }, [root, quality]);
  const currentChord = useMemo(
    () =>
      voicings[voicingIdx] || {
        frets: [0, 2, 2, 1, 0, 0],
        baseFret: 1,
        label: "Default",
      },
    [voicings, voicingIdx]
  );

  // Per-string freqs/midis for active chord
  const stringFreqs = useMemo(
    () =>
      currentChord.frets.map((fret, i) =>
        fret < 0 ? null : freqFor(tuning[i].freq, fret)
      ),
    [currentChord, tuning]
  );
  const stringMidis = useMemo(
    () =>
      currentChord.frets.map((fret, i) =>
        fret < 0 ? null : midiFor(tuning[i].midi, fret)
      ),
    [currentChord, tuning]
  );

  const startAudio = async () => {
    await audioRef.current?.resume();
  };
  const logEvent = (e: {
    kind: "pluck" | "strum";
    s?: number;
    dir?: "down" | "up";
  }) => {
    if (!isRecording) return;
    const now = performance.now();
    if (recordStartRef.current === 0) recordStartRef.current = now;
    const t = (now - recordStartRef.current) / 1000;
    eventsRef.current.push({ t, ...e });
  };
  const pluckString = async (sIdx: number) => {
    await startAudio();
    const f = stringFreqs[sIdx];
    if (f) {
      audioRef.current?.pluck(f);
      logEvent({ kind: "pluck", s: sIdx });
    }
  };
  const strum = async (dir: "down" | "up" = "down") => {
    await startAudio();
    const order = dir === "down" ? [0, 1, 2, 3, 4, 5] : [5, 4, 3, 2, 1, 0];
    let t = 0;
    for (const i of order) {
      const f = stringFreqs[i];
      if (f) {
        audioRef.current?.pluck(f, t);
        t += 0.03;
      }
    }
    logEvent({ kind: "strum", dir });
  };

  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      const map: Record<string, number> = {
        q: 0,
        w: 1,
        e: 2,
        r: 3,
        t: 4,
        y: 5,
      };
      if (map[key] !== undefined) {
        e.preventDefault();
        pluckString(map[key]);
      } else if (key === " ") {
        e.preventDefault();
        strum("down");
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        strum("up");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [stringFreqs]);

  // Loop playback + count-in
  useEffect(() => {
    if (!isLooping) return;
    const evts = [...eventsRef.current];
    if (evts.length === 0) {
      setIsLooping(false);
      return;
    }
    const beatScale = 90 / (bpm || 1);
    let cancelled = false;
    (async () => {
      if (countIn) {
        await startAudio();
        for (let i = 0; i < 4; i++) {
          metroRef.current?.click(i === 0);
          await new Promise((r) => setTimeout(r, 60_000 / bpm));
        }
      }
      while (!cancelled) {
        await startAudio();
        for (const e of evts) {
          if (cancelled) break;
          await new Promise((res) => setTimeout(res, e.t * 1000 * beatScale));
          if (e.kind === "pluck" && e.s !== undefined) {
            const f = stringFreqs[e.s];
            if (f) audioRef.current?.pluck(f);
          } else if (e.kind === "strum") {
            strum(e.dir || "down");
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLooping, bpm, stringFreqs, countIn]);

  // Metronome
  useEffect(() => {
    if (!metroOn) return;
    let beat = 0,
      cancelled = false;
    (async () => {
      while (!cancelled) {
        await startAudio();
        metroRef.current?.click(beat % 4 === 0);
        beat++;
        await new Promise((r) => setTimeout(r, 60_000 / bpm));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [metroOn, bpm]);

  /* === UI / DRAWING === */
  const frets = 15;
  const width = 960,
    height = 340;
  const mL = 86,
    mR = 26,
    mT = 60,
    mB = 60; // extra left margin for string labels
  const fbW = width - mL - mR,
    fbH = height - mT - mB;

  // Horizontal neck, top-down POV: low-E at bottom, high-E at top
  const yFor = (s: number) => mT + fbH - (fbH * s) / 5; // s=0 -> bottom
  const fretX = (f: number) => mL + (fbW * f) / frets;

  // Bold global style
  const boldStyle: React.CSSProperties = { fontWeight: 700 };

  /* === Render === */
  return (
    <div
      className="min-h-screen w-full flex items-center justify-center p-6"
      style={{ background: "#fff" }}
    >
      <div
        className="w-full max-w-6xl rounded-2xl shadow-xl bg-white border border-black/10"
        style={boldStyle}
      >
        <div className="p-5 flex flex-col gap-4">
          {/* HEADER */}
          <header className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-2xl">
              Kato Hammond’s Virtual Guitar Woodshed Reference Board
            </h1>
            <div className="flex items-center gap-3 text-sm">
              <span className="px-2 py-1 rounded bg-gray-100 border">
                Pickup:
              </span>
              {(["neck", "middle", "bridge"] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPickup(p)}
                  className={`px-3 py-1 rounded border text-sm ${
                    pickup === p ? "bg-black text-white" : "bg-white"
                  }`}
                >
                  {p}
                </button>
              ))}
              <div className="ml-3 text-xs text-gray-700">
                Top-down POV • Low-E at bottom
              </div>
            </div>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* FRETBOARD */}
            <div className="lg:col-span-2">
              <div
                className="rounded-xl p-3 bg-white border shadow-inner"
                onMouseUp={() => setDragCapo(false)}
                onMouseLeave={() => setDragCapo(false)}
              >
                <svg
                  viewBox={`0 0 ${width} ${height}`}
                  className="w-full h-auto select-none"
                >
                  {/* Neck */}
                  <rect
                    x={mL}
                    y={mT}
                    width={fbW}
                    height={fbH}
                    rx={14}
                    fill="#f5e1bd"
                    stroke="#8b5a2b"
                  />

                  {/* String labels at nut (reflect tuning + capo) */}
                  {[0, 1, 2, 3, 4, 5].map((s) => {
                    const y = yFor(s);
                    return (
                      <text
                        key={`lbl-${s}`}
                        x={mL - 14}
                        y={y + 4}
                        fontSize={12}
                        fill="#111"
                        textAnchor="end"
                      >
                        {openNames[s]}
                      </text>
                    );
                  })}

                  {/* Frets + numbers (click to set capo) */}
                  {[...Array(frets + 1)].map((_, i) => (
                    <g
                      key={i}
                      onClick={() => setCapo(clamp(i, 0, 12))}
                      style={{ cursor: "pointer" }}
                    >
                      <line
                        x1={fretX(i)}
                        y1={mT}
                        x2={fretX(i)}
                        y2={mT + fbH}
                        stroke={i === 0 && capo === 0 ? "#5b3a1a" : "#9c6c3b"}
                        strokeWidth={i === 0 && capo === 0 ? 6 : 2}
                      />
                      <text
                        x={fretX(i) + 4}
                        y={mT - 12}
                        fontSize={12}
                        fill="#111"
                      >
                        {i}
                      </text>
                      <text
                        x={fretX(i) + 4}
                        y={mT + fbH + 18}
                        fontSize={12}
                        fill="#111"
                      >
                        {i}
                      </text>
                    </g>
                  ))}

                  {/* Position markers */}
                  {[3, 5, 7, 9, 15].map((f) => (
                    <circle
                      key={f}
                      cx={fretX(f - 0.5)}
                      cy={mT + fbH / 2}
                      r={6}
                      fill="#6c4a2f"
                    />
                  ))}
                  {/* 12th double dot */}
                  <g>
                    <circle
                      cx={fretX(12 - 0.7)}
                      cy={mT + fbH / 3}
                      r={6}
                      fill="#6c4a2f"
                    />
                    <circle
                      cx={fretX(12 - 0.3)}
                      cy={mT + (2 * fbH) / 3}
                      r={6}
                      fill="#6c4a2f"
                    />
                  </g>

                  {/* Strings (low-E at bottom) */}
                  {[0, 1, 2, 3, 4, 5].map((s) => {
                    const y = yFor(s);
                    return (
                      <line
                        key={s}
                        x1={mL}
                        y1={y}
                        x2={mL + fbW}
                        y2={y}
                        stroke="#3a3024"
                        strokeWidth={2 + (5 - s) * 0.6}
                      />
                    );
                  })}

                  {/* Capo (draggable) */}
                  {capo > 0 && (
                    <rect
                      x={fretX(capo) - 3}
                      y={mT - 6}
                      width={8}
                      height={fbH + 12}
                      rx={3}
                      fill="#111"
                      onMouseDown={() => setDragCapo(true)}
                      onMouseMove={(e) => {
                        if (!dragCapo) return;
                        const rect = (
                          e.target as SVGRectElement
                        ).ownerSVGElement!.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        let fret = Math.round((x - mL) / (fbW / frets));
                        setCapo(clamp(fret, 0, 12));
                      }}
                      style={{ cursor: "ew-resize" }}
                    />
                  )}

                  {/* Active chord dots with note + interval */}
                  {currentChord.frets.map((fret, s) => {
                    const y = yFor(s);
                    if (fret === -1) {
                      return (
                        <text
                          key={s}
                          x={mL - 22}
                          y={y + 5}
                          fontSize={14}
                          fill="#8b0000"
                        >
                          ×
                        </text>
                      );
                    }
                    const vis = Math.max(0, fret) + capo;
                    const x = fretX(vis - 0.5);
                    const midi = stringMidis[s];
                    const name = midi != null ? NOTE_NAMES[pc(midi)] : "";
                    const interval =
                      midi != null
                        ? DEG_LABEL[
                            (pc(midi) -
                              noteIndex(ALT_NOTE_NAMES[root] || root) +
                              12) %
                              12
                          ]
                        : "";

                    if (fret === 0) {
                      const cx = fretX(Math.max(0, capo) - 0.2);
                      return (
                        <g key={s}>
                          <circle
                            cx={cx}
                            cy={y}
                            r={9}
                            fill="#fff"
                            stroke="#333"
                          />
                          <text x={cx + 12} y={y - 6} fontSize={12} fill="#111">
                            {name}
                          </text>
                          <text
                            x={cx + 12}
                            y={y + 10}
                            fontSize={11}
                            fill="#444"
                          >
                            {interval}
                          </text>
                        </g>
                      );
                    }
                    return (
                      <g key={s}>
                        <circle
                          cx={x}
                          cy={y}
                          r={12}
                          fill="#2b2b2b"
                          stroke="#111"
                        />
                        <circle cx={x} cy={y} r={6.5} fill="#c3e6ff" />
                        <text x={x + 14} y={y - 6} fontSize={12} fill="#111">
                          {name}
                        </text>
                        <text x={x + 14} y={y + 10} fontSize={11} fill="#444">
                          {interval}
                        </text>
                      </g>
                    );
                  })}

                  {/* Play zones (mouse pluck) */}
                  {[0, 1, 2, 3, 4, 5].map((s) => {
                    const y = yFor(s);
                    return (
                      <rect
                        key={`pz-${s}`}
                        x={mL}
                        y={y - 12}
                        width={fbW}
                        height={24}
                        fill="transparent"
                        onMouseDown={() => pluckString(s)}
                        onMouseEnter={(e) => {
                          if (e.buttons === 1) pluckString(s);
                        }}
                        style={{ cursor: "pointer" }}
                      />
                    );
                  })}
                </svg>
              </div>
            </div>

            {/* CONTROLS (bold UI, white bg) */}
            <div className="flex flex-col gap-4" style={boldStyle}>
              {/* Chord + Voicing selector */}
              <div className="rounded-xl border bg-white p-4">
                <div className="text-sm mb-2">Chord</div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <select
                    className="border rounded-lg px-3 py-2"
                    value={root}
                    onChange={(e) => setRoot(e.target.value)}
                  >
                    {[
                      "C",
                      "C#",
                      "Db",
                      "D",
                      "D#",
                      "Eb",
                      "E",
                      "F",
                      "F#",
                      "Gb",
                      "G",
                      "G#",
                      "Ab",
                      "A",
                      "A#",
                      "Bb",
                      "B",
                    ].map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  <select
                    className="border rounded-lg px-3 py-2"
                    value={quality}
                    onChange={(e) => setQuality(e.target.value)}
                  >
                    {[
                      ["maj", "Major"],
                      ["min", "Minor"],
                      ["7", "7"],
                      ["maj7", "Maj7"],
                      ["m7", "m7"],
                      ["sus2", "Sus2"],
                      ["sus4", "Sus4"],
                      ["dim", "Dim"],
                      ["add9", "Add9"],
                    ].map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <button
                    className="px-2 py-1 border rounded"
                    onClick={() => setVoicingIdx((i) => Math.max(0, i - 1))}
                  >
                    ◀
                  </button>
                  <div className="text-xs">
                    Voicing: {currentChord.label} ({voicingIdx + 1}/
                    {voicings.length || 1})
                  </div>
                  <button
                    className="px-2 py-1 border rounded"
                    onClick={() =>
                      setVoicingIdx((i) =>
                        Math.min(voicings.length - 1 || 0, i + 1)
                      )
                    }
                  >
                    ▶
                  </button>
                </div>
              </div>

              {/* Setup */}
              <div className="rounded-xl border bg-white p-4">
                <div className="text-sm mb-2">Setup</div>
                <div className="grid grid-cols-2 gap-2 items-center">
                  <select
                    className="border rounded-lg px-3 py-2 col-span-2"
                    value={tuningId}
                    onChange={(e) => setTuningId(e.target.value)}
                  >
                    {TUNINGS.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <label className="text-sm">Capo: {capo}</label>
                  <input
                    type="range"
                    min={0}
                    max={12}
                    value={capo}
                    onChange={(e) => setCapo(parseInt(e.target.value, 10))}
                  />
                  <label className="text-sm">
                    Sustain: {sustain.toFixed(1)}s
                  </label>
                  <input
                    type="range"
                    min={0.5}
                    max={6}
                    step={0.1}
                    value={sustain}
                    onChange={(e) => setSustain(parseFloat(e.target.value))}
                  />
                </div>
                <div className="text-xs mt-2">
                  Tip: drag the black capo bar or click a fret number.
                </div>
              </div>

              {/* Play / Metronome & Loop */}
              <div className="rounded-xl border bg-white p-4">
                <div className="text-sm mb-2">Play</div>
                <div className="flex gap-2 flex-wrap mb-3">
                  <button
                    className="px-4 py-2 rounded border bg-white"
                    onClick={() => strum("down")}
                  >
                    Strum Down
                  </button>
                  <button
                    className="px-4 py-2 rounded border bg-white"
                    onClick={() => strum("up")}
                  >
                    Strum Up
                  </button>
                  <div className="text-xs">
                    Q–Y = strings low→high • Space = down-strum • ↑ = up-strum
                  </div>
                </div>

                <div className="text-sm mb-1">Metronome & Loop</div>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <button
                    className={`px-3 py-2 rounded border ${
                      metroOn ? "bg-black text-white" : "bg-white"
                    }`}
                    onClick={() => setMetroOn((v) => !v)}
                  >
                    {metroOn ? "Stop Metronome" : "Start Metronome"}
                  </button>
                  <label className="text-sm">BPM: {bpm}</label>
                  <input
                    type="range"
                    min={40}
                    max={200}
                    value={bpm}
                    onChange={(e) => setBpm(parseInt(e.target.value, 10))}
                  />
                  <label className="text-sm">Volume</label>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={metroVol}
                    onChange={(e) => setMetroVol(parseFloat(e.target.value))}
                  />
                  <label className="text-sm flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={countIn}
                      onChange={(e) => setCountIn(e.target.checked)}
                    />{" "}
                    1-bar count-in
                  </label>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    className={`px-3 py-2 rounded border ${
                      isRecording ? "bg-red-600 text-white" : "bg-white"
                    }`}
                    onClick={() => {
                      if (isRecording) {
                        setIsRecording(false);
                      } else {
                        eventsRef.current = [];
                        recordStartRef.current = 0;
                        setIsRecording(true);
                      }
                    }}
                  >
                    {isRecording ? "Stop Recording" : "Start Recording"}
                  </button>
                  <button
                    className={`px-3 py-2 rounded border ${
                      isLooping ? "bg-amber-600 text-white" : "bg-white"
                    }`}
                    onClick={() => setIsLooping((v) => !v)}
                  >
                    {isLooping ? "Stop Loop" : "Play Loop"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <footer className="text-xs pt-2 border-t">
            Clean view + string labels at nut. CAGED voicings, tunings, capo,
            sustain, metronome, loop.
          </footer>
        </div>
      </div>
    </div>
  );
}
